<template>
    <div class="flex flex-col-reverse flex-1 scroll-thin overflow-y-auto gap-6 pb-3" @scroll="handleScroll">
        <div v-if="showSpinner" class="flex items-center justify-center w-full p-6">
            <vx-icon-loading spin></vx-icon-loading>
        </div>

        <div v-if="activeThread && !currentOrgUnit">
            <conversation-scheduled-messages></conversation-scheduled-messages>
        </div>

        <div
            v-if="!FEATURES.simple_message_hub && activeThread?.status === 'closed'"
            class="relative flex items-center justify-center w-full my-4"
        >
            <div class="absolute border-0 border-b border-solid border-rose-300 w-full"></div>
            <vx-badge color="danger" borders size="sm" class="absolute text-center px-3 py-1"> Closed </vx-badge>
        </div>

        <div v-if="showEmptyState" class="flex items-center justify-center w-full p-6">
            <vx-box
                color="info"
                class="w-full bg-slate-100! flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500 font-normal opacity-50"
            >
                No message history
            </vx-box>
        </div>

        <template v-if="activeThread && messageGroups.length">
            <div v-for="messageGroup in messageGroups" :key="messageGroup.prettyDate" class="flex flex-col gap-6">
                <div class="relative flex items-center justify-center w-full my-4">
                    <div
                        class="absolute border-0 border-b border-solid w-full"
                        :class="{
                            'border-gray-200': !messageGroup.lastUnreadInboundMessage,
                            'border-amber-300': messageGroup.lastUnreadInboundMessage,
                        }"
                    ></div>
                    <vx-badge
                        :color="messageGroup.lastUnreadInboundMessage ? 'amber' : 'slate'"
                        borders
                        size="sm"
                        class="absolute text-center px-3 py-1"
                        :class="{
                            'bg-slate-50! text-slate-500!': !messageGroup.lastUnreadInboundMessage,
                        }"
                    >
                        <strong v-if="messageGroup.lastUnreadInboundMessage">New: </strong>
                        {{ messageGroup.prettyDate }}
                    </vx-badge>
                </div>
                <div class="flex flex-col gap-6">
                    <message-cloud
                        v-for="message in messageGroup.messages"
                        :key="message.id"
                        :message="message"
                        :unread="!messageGroup.lastUnreadInboundMessage && shouldShowNewLine(message)"
                        @mediaPlaceholderClick="
                            ($event) => {
                                if ($event.isFileExpired) {
                                    mediaExpiredModal = true;
                                }
                            }
                        "
                    ></message-cloud>
                </div>
            </div>
        </template>

        <expired-media-modal v-model="mediaExpiredModal"></expired-media-modal>

        <slot />
    </div>
</template>
<script setup>
import { ref, computed, inject } from 'vue';
import { VxBadge, VxBox, VxIconLoading } from '@voxie/frontend-components';
import debounce from '~/utils/debounce';
import MessageCloud from './misc/MessageCloud.vue';
import { useConversation } from '~/composables/useConversation';
import ExpiredMediaModal from '~/components/modals/ExpiredMediaModal.vue';
import { useContactDetails } from '~/components/contacts/contact/useContactDetails';
import ConversationScheduledMessages from '~/components/messages/browser-extension/components/ConversationScheduledMessages.vue';
import { useCurrentGroup } from '~/composables/useCurrentGroup';

const FEATURES = inject('FEATURES');

const { currentOrgUnit } = useCurrentGroup();
const { loading: loadingContact } = useContactDetails();
const { activeThread, loadingMessages, mounting, isLast, loadMessages, messageGroups, shouldShowNewLine } =
    useConversation();

const mediaExpiredModal = ref(false);

const showSpinner = computed(() => {
    return (loadingMessages.value || mounting.value || loadingContact.value) && !messageGroups.value.length;
});

const showEmptyState = computed(() => {
    return (
        activeThread.value &&
        !loadingMessages.value &&
        !mounting.value &&
        !loadingContact.value &&
        !messageGroups.value.length
    );
});

const isUserNearTop = (el) => {
    const scrollHeight = el.scrollHeight;
    const threshold = scrollHeight * 0.3;
    const scrollTop = el.scrollTop;
    const clientHeight = el.clientHeight;

    const scrollPosition = (scrollTop - clientHeight) * -1;

    return scrollPosition + threshold >= scrollHeight;
};

const handleScroll = debounce(async (event) => {
    if (!event?.target || loadingMessages.value || isLast.value) {
        return;
    }

    if (isUserNearTop(event.target)) {
        loadMessages();
    }
}, 300);
</script>
